import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { Card, EmptyState } from '../../../components/app2';

/**
 * Gráficas del panel de empresa (rediseño jul-2026).
 *
 * `variant="monthly"` → columna lateral: nóminas por mes (lo que el diseño
 * llama "Nómina mensual").
 * `variant="detail"`  → columna principal: estado de recibos y empleados por
 * área. El diseño no las dibuja, pero son datos reales y se conservan.
 *
 * Cambio de fondo: la serie "recibos" de la gráfica mensual se calculaba como
 * `nóminas * 0.8` —un número inventado— y se retiró. Queda la serie real.
 */

// Paleta del rediseño. Los acentos son los mismos en claro y en oscuro, así que
// pueden ir fijos en la configuración de las gráficas.
const BRAND = '#2F6BFF';
const BRAND_2 = '#6FA0FF';
const OK = '#2BA779';
const WARN = '#E0A138';
const DANGER = '#E5574E';

/**
 * Datos del panel a partir de los PERIODOS REALES de nómina.
 *
 * Antes esto se calculaba sobre `last_payroll_date` y `receipt_status`, dos
 * campos que el backend NUNCA envía (venían del export de diseño). Resultado:
 * la gráfica siempre decía "no hay nóminas procesadas" y la completitud daba 0%
 * aunque la empresa tuviera miles de recibos. Ahora se usa lo que sí existe.
 */
const buildData = (companyData, periodos = []) => {
  const employees = companyData?.employees || [];
  if (employees.length === 0 && periodos.length === 0) {
    return { monthlyData: [], departmentData: [], statusData: [] };
  }

  // Nóminas por mes (últimos 6 meses), contando periodos de nómina reales.
  const monthlyData = [];
  const today = new Date();
  for (let i = 5; i >= 0; i--) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const monthName = date.toLocaleDateString('es-MX', { month: 'short' });
    const nominas = periodos.filter((p) => {
      if (!p?.periodEnd) return false;
      const [y, m] = String(p.periodEnd).slice(0, 10).split('-').map(Number);
      return m - 1 === date.getMonth() && y === date.getFullYear();
    }).length;
    monthlyData.push({ month: monthName, nominas });
  }

  // Empleados por área
  const departmentCounts = {};
  employees.forEach((emp) => {
    const dept = emp?.department || 'No especificado';
    departmentCounts[dept] = (departmentCounts[dept] || 0) + 1;
  });
  const departmentData = Object.entries(departmentCounts).map(([department, count]) => ({
    department: department.length > 15 ? `${department.substring(0, 15)}…` : department,
    empleados: count,
  }));

  // Cobertura del último periodo: cuántos colaboradores tienen recibo y cuántos
  // no. Sustituye al "estado del recibo" (descargado/visto/pendiente), que no
  // existe: el sistema no registra si alguien abrió su recibo, así que la dona
  // ponía a TODOS en "Pendiente". Esto sí es accionable — dice a quién le falta.
  const ultimoPeriodo = periodos[0];
  const conRecibo = ultimoPeriodo?.employees || 0;
  const activos = employees.filter((e) => e?.is_active !== false).length;
  const sinRecibo = Math.max(activos - conRecibo, 0);
  const statusData = ultimoPeriodo
    ? [
      { name: 'Con recibo', value: conRecibo, color: OK },
      { name: 'Sin recibo', value: sinRecibo, color: DANGER },
    ].filter((item) => item.value > 0)
    : [];

  return { monthlyData, departmentData, statusData };
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: 'var(--surface)', border: '1px solid var(--line-2)', borderRadius: 10,
        boxShadow: 'var(--shadow)', padding: '10px 12px',
      }}
    >
      <div style={{ font: "700 .82rem 'Hanken Grotesk'", color: 'var(--heading)' }}>{label}</div>
      {payload.map((entry, i) => (
        <div key={i} style={{ font: "500 .82rem 'Hanken Grotesk'", color: entry?.color || 'var(--text)' }}>
          {entry?.name}: {entry?.value}
        </div>
      ))}
    </div>
  );
};

const AXIS = { fontSize: 12, stroke: 'var(--muted)', tickLine: false, axisLine: false };

/* El eje Y necesita ancho real: con márgenes izquierdos negativos las etiquetas
   de tres cifras se recortaban y solo quedaba visible su último dígito (se veían
   cinco "0" en lugar de 0/200/400/600/800). */
const Y_WIDTH = 44;

const PayrollChartsPanel = ({ companyData, periodos = [], variant = 'detail' }) => {
  const { monthlyData, departmentData, statusData } = buildData(companyData, periodos);
  const hayNominas = monthlyData.some((m) => m.nominas > 0);

  // ── Columna lateral: nóminas por mes ───────────────────────────────────────
  if (variant === 'monthly') {
    return (
      <Card pad={false} style={{ padding: 20 }}>
        <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Nómina mensual</h3>
        <p className="a2-card-sub" style={{ marginBottom: 16 }}>Nóminas procesadas por mes</p>
        {hayNominas ? (
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={monthlyData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
              <XAxis dataKey="month" {...AXIS} />
              <YAxis {...AXIS} allowDecimals={false} width={Y_WIDTH} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--hover)' }} />
              <Bar dataKey="nominas" name="Nóminas" fill={BRAND} radius={[6, 6, 2, 2]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ font: "500 .84rem 'Hanken Grotesk'", color: 'var(--muted)', margin: 0 }}>
            Todavía no hay nóminas procesadas en los últimos seis meses.
          </p>
        )}
      </Card>
    );
  }

  // ── Columna principal: estado de recibos + empleados por área ──────────────
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 16 }}>
      <Card>
        <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Cobertura del último periodo</h3>
        <p className="a2-card-sub" style={{ marginBottom: 16 }}>Colaboradores activos con recibo</p>
        {statusData.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <ResponsiveContainer width="55%" height={180} minWidth={150}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={42} outerRadius={70} dataKey="value" label={false} stroke="none">
                  {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, minWidth: 130, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {statusData.map((entry) => (
                <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
                  <span style={{ font: "600 .86rem 'Hanken Grotesk'", color: 'var(--heading)' }}>{entry.name}</span>
                  <span className="a2-mono" style={{ marginLeft: 'auto', fontSize: '.86rem', color: 'var(--muted)' }}>
                    {entry.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <EmptyState icon="donut_large" title="Sin recibos" text="Aún no hay recibos que clasificar." className="a2-card" style={{ border: 'none', padding: '28px 12px' }} />
        )}
      </Card>

      <Card>
        <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Empleados por área</h3>
        <p className="a2-card-sub" style={{ marginBottom: 16 }}>
          {companyData?.company_name ? `Distribución en ${companyData.company_name}` : 'Distribución organizacional'}
        </p>
        {departmentData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={departmentData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
              <XAxis dataKey="department" {...AXIS} angle={-35} textAnchor="end" height={60} interval={0} />
              <YAxis {...AXIS} allowDecimals={false} width={Y_WIDTH} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--hover)' }} />
              <Bar dataKey="empleados" name="Empleados" fill={BRAND_2} radius={[6, 6, 2, 2]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState icon="domain_disabled" title="Sin áreas" text="Los colaboradores aún no tienen área asignada." className="a2-card" style={{ border: 'none', padding: '28px 12px' }} />
        )}
      </Card>
    </div>
  );
};

export default PayrollChartsPanel;
